import { useSelector } from "react-redux";
import { RootState } from "../../store";
import React, { useEffect, useRef, useState } from "react";
import {
  CreditNote,
  CREDIT_NOTE_REASON_RETURN_AND_PRICE_ADJUSTMENT,
} from "../../models/credit-note";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { useHistory } from "react-router-dom";
import { errorAlert, successAlert } from "../utils/messages";
import Loading from "../indicators/Loading";
import ClientUpdateModal from "../client/ClientUpdateModal";
import { Spinner } from "react-bootstrap";
import { getRoutePath } from "../../routes";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import { getClientForDocument } from "../../api/client";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import { getInvoiceById } from "../../api/invoice";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import Client from "../../models/client";
import { PYG } from "../../currency/available-currencies";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";

// Context provider
import {
  CreditNoteFormProvider,
  useCreditNoteFormContext,
} from "./context/CreditNoteFormContext";

// Section components
import { CreditNoteFormHeader } from "./sections/CreditNoteFormHeader";
import { CreditNoteFormDetailsSection } from "./sections/CreditNoteFormDetailsSection";
import { CreditNoteFormAssociatedDocumentSection } from "./sections/CreditNoteFormAssociatedDocumentSection";
import { CreditNoteFormTotals } from "./sections/CreditNoteFormTotals";
import { CreditNoteFormItems } from "./sections/CreditNoteFormItems";

interface CreditNoteFormProps {
  readonly?: boolean;
}

// Inner component that uses the context
const CreditNoteFormContent: React.FC<{
  readonly?: boolean;
}> = ({ readonly }) => {
  const [showAddClient, setShowAddClient] = useState(false);
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);

  const history = useHistory();

  const {
    creditNoteRef,
    setCreditNoteDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setClient,
    setCurrency,
    setReasonCode,
    setPygExchangeRate,
    setAssociatedDocumentType,
    setElectronicInvoice,
    onItemsChange,
    setErrors,
  } = useCreditNoteFormContext();

  const getEditingCreditNoteId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingCreditNoteId = urlParams.get("id");

    if (editingCreditNoteId !== null) {
      if (!isNaN(parseInt(editingCreditNoteId))) {
        return parseInt(editingCreditNoteId);
      }
    }

    return undefined;
  };

  // Auto-select stamp, establishment and dispatch point if only one available
  useEffect(() => {
    const editingCreditNoteId = getEditingCreditNoteId();
    if (editingCreditNoteId !== undefined) {
      return;
    }

    if (stamps.length === 1 && creditNoteRef.current?.stampId === undefined) {
      const stamp = stamps[0];
      if (creditNoteRef.current) {
        creditNoteRef.current.stampId = stamp.id;
        creditNoteRef.current.stampIdentifier = stamp.identifier;
        creditNoteRef.current.stampBeginDate = stamp.beginDate;
      }
      setStamp(stamp);
    }

    if (
      establishments.length === 1 &&
      creditNoteRef.current?.establishmentId === undefined
    ) {
      const establishment = establishments[0];
      if (creditNoteRef.current) {
        creditNoteRef.current.establishmentId = establishment.id;
        creditNoteRef.current.establishmentCode = establishment.code;
      }
      setEstablishment(establishment);
    }

    if (
      dispatchPoints.length === 1 &&
      creditNoteRef.current?.dispatchPointId === undefined
    ) {
      const dispatchPoint = dispatchPoints[0];
      if (creditNoteRef.current) {
        creditNoteRef.current.dispatchPointId = dispatchPoint.id;
        creditNoteRef.current.dispatchPointIdentifier =
          dispatchPoint.identifier;
      }
      setDispatchPoint(dispatchPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps, establishments, dispatchPoints]);

  const loadInitialCreditNoteById = async (): Promise<CreditNote | null> => {
    const editingCreditNoteId = getEditingCreditNoteId();

    if (editingCreditNoteId === undefined) {
      return null;
    }

    let requestStatus = await getItem<CreditNote>(
      `/credit_notes/${editingCreditNoteId}/`
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const creditNote = await loadInitialCreditNoteById();

    if (creditNote === null) {
      setLoading(false);
      return;
    }

    const stampPromise = getStampById(
      creditNote.stampId,
      creditNote.taxPayerId
    );
    const establishmentPromise = getEstablishmentById(
      creditNote.establishmentId,
      creditNote.taxPayerId
    );
    const dispatchPointPromise = getDispatchPointById(
      creditNote.dispatchPointId,
      creditNote.taxPayerId
    );
    const clientPromise = getClientForDocument(creditNote);

    let invoicePromise = null;
    if (creditNote.invoiceId !== undefined && creditNote.invoiceId !== null) {
      invoicePromise = getInvoiceById(
        creditNote.invoiceId,
        creditNote.taxPayerId
      );
    }

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const client = await clientPromise;
    const invoice = invoicePromise !== null ? await invoicePromise : null;

    creditNoteRef.current = creditNote;

    const pygDate = new Date(creditNote.creditNoteDate!);
    const year = pygDate.getUTCFullYear();
    const month = pygDate.getUTCMonth();
    const day = pygDate.getUTCDate();
    const hours = pygDate.getUTCHours();
    const minutes = pygDate.getUTCMinutes();
    const seconds = pygDate.getUTCSeconds();
    const creditNoteDate = new Date(year, month, day, hours, minutes, seconds);
    setCreditNoteDate(creditNoteDate);
    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    setClient(client);
    setAssociatedDocumentType(creditNote.associatedDocumentType);
    setElectronicInvoice(invoice);
    onItemsChange(creditNote.items);

    setCurrency(creditNote.currency ? creditNote.currency : PYG);
    setReasonCode(
      creditNote.reasonCode ?? CREDIT_NOTE_REASON_RETURN_AND_PRICE_ADJUSTMENT
    );
    setPygExchangeRate(creditNote.pygExchangeRate);

    setLoading(false);
  };

  useEffect(() => {
    const newCreditNoteDate = changeTZToPyg(new Date());
    if (creditNoteRef.current !== null) {
      creditNoteRef.current.creditNoteDate =
        changeTZToUtc(newCreditNoteDate).toISOString();
      setCreditNoteDate(newCreditNoteDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddClientSuccess = (newClient?: Client) => {
    let message = "Cliente agregado correctamente";
    if (newClient) {
      message = `Cliente ${newClient.socialName} agregado correctamente`;
      if (creditNoteRef.current) {
        creditNoteRef.current.clientId = newClient.id;
        creditNoteRef.current.clientCode = newClient.code;
        creditNoteRef.current.clientIsTaxPayer = newClient.isTaxPayer;
        creditNoteRef.current.clientRuc = newClient.ruc;
        creditNoteRef.current.clientSocialName = newClient.socialName;
        creditNoteRef.current.clientFantasyName = newClient.fantasyName;
        creditNoteRef.current.clientType = newClient.type;
        creditNoteRef.current.clientCountryCode = newClient.countryCode;
        creditNoteRef.current.clientHasAddress = newClient.hasAddress;
        creditNoteRef.current.clientAddress = newClient.address;
        creditNoteRef.current.clientHouseNumber = newClient.houseNumber;
        creditNoteRef.current.clientDepartment = newClient.department;
        creditNoteRef.current.clientDepartmentDescription =
          newClient.departmentDescription;
        creditNoteRef.current.clientDistrict = newClient.district;
        creditNoteRef.current.clientDistrictDescription =
          newClient.districtDescription;
        creditNoteRef.current.clientCity = newClient.city;
        creditNoteRef.current.clientCityDescription = newClient.cityDescription;
        creditNoteRef.current.clientPhoneNumber = newClient.phoneNumber;
        creditNoteRef.current.clientEmail = newClient.email;
      }
      setClient(newClient);
    }
    successAlert(message);
    setShowAddClient(false);
  };

  const onSave = async () => {
    if (creditNoteRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<CreditNote>>;
    let successMessage = "Nota de Crédito agregada exitósamente";

    const creditNote = creditNoteRef.current;
    creditNote.taxPayerRuc = taxPayer.ruc;
    creditNote.taxPayerSocialName = taxPayer.socialName;
    creditNote.taxPayerFantasyName = taxPayer.fantasyName;

    if (creditNote.currency === undefined) {
      creditNote.currency = PYG;
    }

    if (creditNote.id === undefined) {
      requestPromise = createItem<CreditNote>(
        "/credit_notes/create/",
        creditNote,
        { "Idempotency-Key": idempotencyKey },
      );
    } else {
      requestPromise = updateItem<CreditNote>(
        `/credit_notes/${creditNote.id}/`,
        creditNote
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("creditNotes"));
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          className="d-flex align-items-center"
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <i className="fa fa-file-text-o mr-2" style={{ fontSize: "1.1rem" }}></i>
          <span style={{ fontWeight: 600, fontSize: "1rem" }}>
            {creditNoteRef.current !== null &&
            creditNoteRef.current.id !== undefined ? (
              <>
                {readonly ? "Ver" : "Editar"} Nota de Crédito Electrónica{" "}
                <span style={{ opacity: 0.85 }}>#{creditNoteRef.current.id}</span>
              </>
            ) : (
              <>Agregar Nota de Crédito Electrónica</>
            )}
          </span>
        </CCardHeader>
        <CCardBody style={{ padding: "24px" }}>
          <fieldset disabled={readonly}>
            <CreditNoteFormHeader
              onAddClientClick={() => setShowAddClient(true)}
            />
            <CreditNoteFormAssociatedDocumentSection />
            <CreditNoteFormDetailsSection />
            <CreditNoteFormTotals />
            <CreditNoteFormItems />
          </fieldset>
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          {readonly !== true ? (
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting}
              style={{ minWidth: "130px", fontWeight: 600, padding: "8px 20px" }}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <i className="fa fa-save mr-2"></i>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          ) : (
            <></>
          )}
        </CCardFooter>
      </CCard>
      <ClientUpdateModal
        show={showAddClient}
        onSuccess={onAddClientSuccess}
        onCancel={() => setShowAddClient(false)}
      />
    </>
  );
};

// Main wrapper component
const CreditNoteForm: React.FC<CreditNoteFormProps> = ({ readonly }) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  return (
    <CreditNoteFormProvider taxPayerId={taxPayer.id} readonly={readonly}>
      <CreditNoteFormContent readonly={readonly} />
    </CreditNoteFormProvider>
  );
};

export default CreditNoteForm;
